import { useMutation, useQuery } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { Loader2, ShieldCheck } from "lucide-react"
import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { useSession } from "@/entities/session"
import { approveConsent, previewConsent, readConsentRequest } from "@/pages/mcp/api/mcp"
import { Button } from "@/shared/ui/button"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { LoadSwap } from "@/shared/ui/load-swap"
import { PageShell } from "@/shared/ui/page-shell"
import { Reveal } from "@/shared/ui/reveal"
import { Skeleton } from "@/shared/ui/skeleton"

const ABILITIES = [
	"читать посты, ботов, рассылки, аудиторию, аналитику",
	"править черновики сценариев, рассылок и постов",
	"публиковать, рассылать и удалять только после вашего «да» в чате",
]

/** Заглушка запроса: строки заголовка, пояснения, списка прав и кнопки на своих местах. */
function ConsentSkeleton() {
	return (
		<div aria-hidden="true" className="space-y-4">
			<div className="space-y-1.5">
				<Skeleton className="h-5 w-full rounded" />
				<Skeleton className="h-5 w-2/3 rounded" />
			</div>
			<div className="space-y-1.5">
				<Skeleton className="h-3.5 w-full rounded" />
				<Skeleton className="h-3.5 w-1/2 rounded" />
			</div>
			<div className="space-y-2">
				{[80, 70, 90].map((width) => (
					<Skeleton key={width} className="h-3.5 rounded" style={{ width: `${width}%` }} />
				))}
			</div>
			<Skeleton className="h-3 w-56 rounded" />
			<div className="flex gap-2">
				<Skeleton className="h-12 w-32 rounded-xl" />
				<Skeleton className="h-12 w-28 rounded-xl" />
			</div>
		</div>
	)
}

/** Экран согласия: ИИ-клиент просит работать с бэкофисом от имени сотрудника. */
export function ConsentPage() {
	const [params] = useSearchParams()
	const { user } = useSession()
	const request = useMemo(() => readConsentRequest(params), [params])
	const preview = useQuery({
		queryKey: ["mcp", "consent", request],
		queryFn: () => previewConsent(request as NonNullable<typeof request>),
		enabled: !!request,
		retry: false,
	})
	const approve = useMutation({
		mutationFn: () => approveConsent(request as NonNullable<typeof request>),
		onSuccess: ({ redirectTo }) => window.location.assign(redirectTo),
	})

	const deny = () => {
		if (!request) return
		const back = new URL(request.redirect_uri)
		back.searchParams.set("error", "access_denied")
		if (request.state) back.searchParams.set("state", request.state)
		if (preview.data?.issuer) back.searchParams.set("iss", preview.data.issuer)
		window.location.assign(back.toString())
	}

	const failed = !request || preview.isError

	return (
		<PageShell title="Доступ для ИИ" width="narrow">
			<LoadSwap loading={!failed && preview.isLoading} id={failed ? "error" : "ready"}>
				{failed ? (
					<LoadErrorState
						message={
							preview.isError
								? extractApiErrorMessage(preview.error, "Запрос на доступ не прошёл проверку.")
								: "В ссылке нет запроса на доступ. Начните подключение в Claude заново."
						}
					/>
				) : (
					<section className="space-y-4 rounded-2xl bg-card p-6 ring-1 ring-border">
						<ShieldCheck className="size-8 text-primary" />
						{preview.isLoading ? (
							<ConsentSkeleton />
						) : (
							<>
								<p className="text-lg font-semibold">
									{preview.data?.clientName} просит доступ к бэкофису от вашего имени
								</p>
								<p className="text-sm text-muted-foreground">
									Вы вошли как {user?.email ?? user?.username ?? user?.firstName}. Вход вернётся на{" "}
									{preview.data?.redirectHost}. Он сможет:
								</p>
								<ul className="space-y-1 text-sm">
									{ABILITIES.map((ability) => (
										<li key={ability}>• {ability}</li>
									))}
								</ul>
								<p className="text-xs text-muted-foreground">
									Отключить можно в любой момент в разделе «MCP».
								</p>
								<div>
									<Reveal show={approve.isError} className="pb-4">
										<p role="alert" className="text-sm text-destructive">
											{extractApiErrorMessage(approve.error, "Не удалось выдать доступ")}
										</p>
									</Reveal>
									<div className="flex gap-2">
										<Button onClick={() => approve.mutate()} disabled={approve.isPending}>
											{approve.isPending ? <Loader2 className="animate-spin" /> : null}
											Разрешить
										</Button>
										<Button variant="ghost" onClick={deny}>
											Отказать
										</Button>
									</div>
								</div>
							</>
						)}
					</section>
				)}
			</LoadSwap>
		</PageShell>
	)
}
